import { Head, Link } from "@inertiajs/react"
import { AppShell } from "@/components/AppShell"
import { PageHeader } from "@/components/PageHeader"
import { FlashNotice } from "@/components/admin/FlashNotice"
import { Pagination } from "@/components/Pagination"
import { WeeklyReportsSubNav } from "@/components/weekly_reports/WeeklyReportsSubNav"
import { Button } from "@/components/ui/button"
import {
  Table,
  TableBody,
  TableCell,
  TableEmpty,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "@/components/ui/table"
import type { Pagination as PaginationMeta } from "@/types/run"
import type { WeeklyReportRow } from "@/types/weekly_report"

const DESCRIPTION =
  "Every weekly activity report you have filed in the Portal, newest first, each one open to read and to correct."

export default function WeeklyReportsIndex({
  reports,
  pagination,
  can_edit,
}: {
  reports: WeeklyReportRow[]
  pagination: PaginationMeta
  /** False with Read only: reports open to read rather than to correct. */
  can_edit: boolean
}) {
  const open = (id: number) => (can_edit ? `/weekly_reports/${id}/edit` : `/weekly_reports/${id}`)

  return (
    <>
      <Head title="Past Reports">
        <meta name="description" content={DESCRIPTION} />
        <meta property="og:title" content="Past Reports" />
        <meta property="og:description" content={DESCRIPTION} />
      </Head>
      <AppShell>
        <PageHeader
          title="Weekly Reports"
          description="Everything you have filed, newest first. Any report stays open to correction."
          tabs={<WeeklyReportsSubNav />}
        />

        <div className="mt-6">
          <FlashNotice />

          {reports.length === 0 ? (
            <div className="callout callout-accent">
              <p className="text-sm">
                You have not filed a report in the Portal yet.
              </p>
              {can_edit && (
                <div className="mt-3">
                  <Button asChild>
                    <Link href="/weekly_reports/new">File this week&apos;s report</Link>
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeaderCell>Week</TableHeaderCell>
                    <TableHeaderCell>Worked in</TableHeaderCell>
                    <TableHeaderCell>Filed</TableHeaderCell>
                    <TableHeaderCell>Last updated</TableHeaderCell>
                    <TableHeaderCell>
                      <span className="sr-only">Open</span>
                    </TableHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reports.length === 0 && <TableEmpty colSpan={5}>No reports.</TableEmpty>}
                  {reports.map((report) => (
                    <TableRow key={report.id}>
                      <TableCell nowrap>
                        <Link href={open(report.id)}>{report.week_label}</Link>
                      </TableCell>
                      <TableCell>
                        {report.worked_in.length > 0 ? (
                          report.worked_in.join(", ")
                        ) : (
                          <span className="text-ink-muted">—</span>
                        )}
                      </TableCell>
                      <TableCell nowrap>
                        {report.submitted_at ? (
                          formatStamp(report.submitted_at)
                        ) : (
                          <span className="text-ink-muted">—</span>
                        )}
                      </TableCell>
                      <TableCell nowrap>{formatStamp(report.updated_at)}</TableCell>
                      <TableCell nowrap>
                        <Link href={open(report.id)}>Open</Link>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <Pagination meta={pagination} path="/weekly_reports" label="reports" />
            </>
          )}
        </div>
      </AppShell>
    </>
  )
}

function formatStamp(value: string) {
  return new Date(value).toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  })
}
